import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';

// Define all possible project statuses with their configurations
export const PROJECT_STATUSES = [
  {
    id: 'project_initiated',
    label: 'Project Initiated',
    icon: '🚀',
    color: 'text-green-800 dark:text-green-100',
    bgColor: 'bg-green-100 dark:bg-green-900',
    hoverBgColor: 'hover:bg-green-200 dark:hover:bg-green-800',
    borderColor: 'border-green-200 dark:border-green-700',
    badgeColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 hover:bg-green-200 dark:hover:bg-green-800'
  },
  {
    id: 'site_assessment',
    label: 'Site Assessment',
    icon: '📏',
    color: 'text-green-800 dark:text-green-100',
    bgColor: 'bg-green-100 dark:bg-green-900',
    hoverBgColor: 'hover:bg-green-200 dark:hover:bg-green-800',
    borderColor: 'border-green-200 dark:border-green-700',
    badgeColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 hover:bg-green-200 dark:hover:bg-green-800'
  },
  {
    id: 'permits_approvals',
    label: 'Permits & Approvals',
    icon: '📜',
    color: 'text-green-800 dark:text-green-100',
    bgColor: 'bg-green-100 dark:bg-green-900',
    hoverBgColor: 'hover:bg-green-200 dark:hover:bg-green-800',
    borderColor: 'border-green-200 dark:border-green-700',
    badgeColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 hover:bg-green-200 dark:hover:bg-green-800'
  },
  {
    id: 'materials_ordered',
    label: 'Materials Ordered',
    icon: '📦',
    color: 'text-orange-800 dark:text-orange-100',
    bgColor: 'bg-orange-100 dark:bg-orange-900',
    hoverBgColor: 'hover:bg-orange-200 dark:hover:bg-orange-800',
    borderColor: 'border-orange-200 dark:border-orange-700',
    badgeColor: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100 hover:bg-orange-200 dark:hover:bg-orange-800'
  },
  {
    id: 'installation_begins',
    label: 'Installation Begins',
    icon: '🔨',
    color: 'text-blue-800 dark:text-blue-100',
    bgColor: 'bg-blue-100 dark:bg-slate-800',
    hoverBgColor: 'hover:bg-blue-200 dark:hover:bg-blue-800',
    borderColor: 'border-blue-200 dark:border-blue-700',
    badgeColor: 'bg-blue-100 text-blue-800 dark:bg-slate-800 dark:text-slate-100 hover:bg-blue-200 dark:hover:bg-slate-700'
  },
  {
    id: 'quality_inspection',
    label: 'Quality Inspection',
    icon: '🔍',
    color: 'text-orange-800 dark:text-orange-100',
    bgColor: 'bg-orange-100 dark:bg-orange-900',
    hoverBgColor: 'hover:bg-orange-200 dark:hover:bg-orange-800',
    borderColor: 'border-orange-200 dark:border-orange-700',
    badgeColor: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100 hover:bg-orange-200 dark:hover:bg-orange-800'
  },
  {
    id: 'completed',
    label: 'Completed',
    icon: '✅',
    color: 'text-green-800 dark:text-green-100',
    bgColor: 'bg-green-100 dark:bg-green-900',
    hoverBgColor: 'hover:bg-green-200 dark:hover:bg-green-800',
    borderColor: 'border-green-200 dark:border-green-700',
    badgeColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 hover:bg-green-200 dark:hover:bg-green-800'
  },
  {
    id: 'pending',
    label: 'Pending',
    icon: '⏳',
    color: 'text-green-800 dark:text-green-100',
    bgColor: 'bg-green-100 dark:bg-green-900',
    hoverBgColor: 'hover:bg-green-200 dark:hover:bg-green-800',
    borderColor: 'border-green-200 dark:border-green-700',
    badgeColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 hover:bg-green-200 dark:hover:bg-green-800'
  },
  {
    id: 'in_progress',
    label: 'In Progress',
    icon: '🔨',
    color: 'text-blue-800 dark:text-blue-100',
    bgColor: 'bg-blue-100 dark:bg-slate-800',
    hoverBgColor: 'hover:bg-blue-200 dark:hover:bg-blue-800',
    borderColor: 'border-blue-200 dark:border-blue-700',
    badgeColor: 'bg-blue-100 text-blue-800 dark:bg-slate-800 dark:text-slate-100 hover:bg-blue-200 dark:hover:bg-slate-700'
  },
  {
    id: 'on_hold',
    label: 'On Hold',
    icon: '⏸️',
    color: 'text-red-800 dark:text-red-100',
    bgColor: 'bg-red-100 dark:bg-red-900',
    hoverBgColor: 'hover:bg-red-200 dark:hover:bg-red-800',
    borderColor: 'border-red-200 dark:border-red-700',
    badgeColor: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 hover:bg-red-200 dark:hover:bg-red-800'
  },
  {
    id: 'cancelled',
    label: 'Cancelled',
    icon: '❌',
    color: 'text-red-800 dark:text-red-100',
    bgColor: 'bg-red-100 dark:bg-red-900',
    hoverBgColor: 'hover:bg-red-200 dark:hover:bg-red-800',
    borderColor: 'border-red-200 dark:border-red-700',
    badgeColor: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 hover:bg-red-200 dark:hover:bg-red-800'
  }
] as const;

export type ProjectStatus = typeof PROJECT_STATUSES[number]['id'];

interface StatusDropdownProps {
  currentStatus?: string;
  onStatusChange: (status: ProjectStatus) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'dropdown' | 'badge';
}

export default function StatusDropdown({
  currentStatus,
  onStatusChange,
  disabled = false,
  size = 'md',
  variant = 'dropdown'
}: StatusDropdownProps) {
  const [open, setOpen] = useState(false);

  // Find the current status configuration
  const currentStatusConfig = PROJECT_STATUSES.find(
    status => status.id === currentStatus
  ) || PROJECT_STATUSES.find(status => status.id === 'pending');

  const handleStatusSelect = (status: ProjectStatus) => {
    onStatusChange(status);
    setOpen(false);
  };

  // If variant is 'badge', just return the badge
  if (variant === 'badge') {
    return (
      <Badge className={currentStatusConfig?.badgeColor}>
        {currentStatusConfig?.icon} {currentStatusConfig?.label}
      </Badge>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          size={size}
          disabled={disabled}
          className={`${size === 'sm' ? 'min-w-[140px] h-8 text-xs' : 'min-w-[180px]'} justify-between ${currentStatusConfig?.badgeColor} border-0 font-semibold`}
        >
          <div className="flex items-center gap-2">
            <span>{currentStatusConfig?.icon}</span>
            <span>{currentStatusConfig?.label}</span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 p-1 bg-white dark:bg-slate-800 shadow-lg border border-gray-200 dark:border-slate-600 max-h-80 overflow-y-auto">
        {PROJECT_STATUSES.map((status, index) => (
          <DropdownMenuItem
            key={status.id}
            onClick={() => handleStatusSelect(status.id as ProjectStatus)}
            className={`flex items-center gap-3 cursor-pointer ${status.bgColor} ${status.hoverBgColor} ${status.color} border-0 rounded-md px-4 py-2.5 mb-1 transition-all font-medium shadow-sm hover:shadow-md`}
          >
            <div className="flex items-center gap-2 flex-1">
              <span className="text-xl">{status.icon}</span>
              <span className="text-sm font-semibold">{status.label}</span>
            </div>
            {currentStatus === status.id && (
              <Check className={`h-4 w-4 font-bold ${status.color}`} />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Helper function to get status configuration
export function getStatusConfig(statusId: string) {
  return PROJECT_STATUSES.find(status => status.id === statusId);
}

// Helper function to get status badge
export function getStatusBadge(statusId: string) {
  const config = getStatusConfig(statusId);
  if (!config) {
    return <Badge variant="secondary">Unknown</Badge>;
  }
  
  return (
    <Badge className={config.badgeColor}>
      {config.icon} {config.label}
    </Badge>
  );
}
